<template>
  <ul class="pagination" :class="{ disabled, wide }">
    <li
      v-for="item in paginationMask"
      :key="item.key"
      class="element-wrap"
      :class="[item.class, { disabled: item.disabled }]"
    >
      <nuxt-link
        v-if="isLink(item)"
        :to="{
          path: getPath(),
          query: getQuery(item),
        }"
        class="element"
        event=""
        @click="onEvent(item)"
      >
        <template v-if="item.key === 'prev'">
          <span class="text">Предыдущая</span>
          <span class="slider_triangle slider_triangle--left">
            <span class="slider_triangle__elem" />
          </span>
        </template>
        <template v-else-if="item.key === 'next'">
          <span class="text">Следующая</span>
          <span class="slider_triangle slider_triangle--right">
            <span class="slider_triangle__elem" />
          </span>
        </template>
        <template v-else>
          {{ item.value }}
        </template>
      </nuxt-link>
      <span v-else class="element" @click="onEvent(item)">
        <span>
          <template v-if="item.key === 'prev'">
            <span class="text">Предыдущая</span>
            <span class="slider_triangle slider_triangle--left">
              <span class="slider_triangle__elem" />
            </span>
          </template>
          <template v-else-if="item.key === 'next'">
            <span class="text">Следующая</span>
            <span class="slider_triangle slider_triangle--right">
              <span class="slider_triangle__elem" />
            </span>
          </template>
          <template v-else>
            {{ item.value }}
          </template>
        </span>
      </span>
    </li>
  </ul>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import type { PaginationElement } from '~/interfaces/app';
import { useAnalyticsStore } from '~/stores/analytics';

/**
 * Функция логики формирования массива кнопок пагинации
 * @param {number} count количество страниц
 * @param {number} active текущаня страница
 **/
function setPaginationMask(
  count: number,
  active: number
): PaginationElement[] | null {
  // Создание типового элемента массива
  const getPaginationElement = (
    className: string,
    value: string
  ): PaginationElement => ({
    class: className,
    value,
    key: value,
  });

  // создание группы кнопок start -> end
  const createPaginationElements = (
    start: number,
    end: number
  ): PaginationElement[] => {
    return Array.from(
      { length: end - start + 1 },
      (_, i): PaginationElement =>
        getPaginationElement(
          'page' + (i + start === active ? ' active' : ''),
          (i + start).toString()
        )
    );
  };

  const nextEl: PaginationElement = {
    class: 'control next',
    disabled: active === count,
    value: active + 1 + '',
    key: 'next',
  };

  const prevEl: PaginationElement = {
    class: 'control prev',
    disabled: active === 1,
    value: active - 1 + '',
    key: 'prev',
  };

  const ellipsisEl: PaginationElement = {
    class: 'ellipsis',
    value: '...',
  };

  if (count > 1 && active <= count && active > 0) {
    if (count <= 7)
      // щаблон массива если страниц меньше чем 7
      return [prevEl, ...createPaginationElements(1, count), nextEl];
    // щаблон массива с одним плавющим многоточием
    else if (active < 5 || active > count - 4) {
      let startElems: number, endElems: number;
      if (active < 3 || active > count - 2) {
        startElems = 3;
        endElems = 3;
      } else {
        startElems = active < 5 ? active + 1 : 4 - count + active;
        endElems = 6 - startElems;
      }

      return [
        prevEl,
        ...createPaginationElements(1, startElems),
        ellipsisEl,
        ...createPaginationElements(count - endElems + 1, count),
        nextEl,
      ];
    }
    // шаблон массива с двумя многоточиями
    else
      return [
        prevEl,
        getPaginationElement('page', '1'),
        ellipsisEl,
        ...createPaginationElements(active - 1, active + 1),
        ellipsisEl,
        getPaginationElement('page', count.toString()),
        nextEl,
      ];
  } else {
    return null;
  }
}

/**
 * Pagination принимает количество страниц и текущую страницу
 * возвращает массив кнопок для пагинации или null если страница одна
 * Массив кнопок длинною от 4 до 9 кнопои
 * Первая и последняя кнопки всегда одинаковые
 * При максимальной длине имеет одно плавающие многоточие, либо два (в начале и в конце)
 * @param {number} pageCount количество страниц
 * @param {number} active текущаня страница
 * @param {boolean} disabled блокировка пагинации
 * @param {boolean} wide ширина пагинации (широкая с надписями "Предыдущая"/"Следующая" true)
 * @param {boolean} links кнопки ссылкой <a> или через @click <span>
 **/
export default defineComponent({
  name: 'PaginationOffers',
  props: {
    pageCount: {
      type: Number,
      required: true,
    },
    active: {
      type: Number,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    wide: {
      type: Boolean,
      default: false,
    },
    links: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['onPageChange'],
  setup(props, { emit }) {
    const analyticsStore = useAnalyticsStore();
    const route = useRoute();

    const paginationMask = computed((): PaginationElement[] | null =>
      setPaginationMask(props.pageCount, props.active)
    );

    const onEvent = (item: PaginationElement) => {
      analyticsStore.clearCpcShows();
      if (item.class !== 'ellipsis') {
        emit('onPageChange', +item.value);
      }
    };

    const isLink = (item: PaginationElement) => {
      return (
        props.links &&
        +item.value &&
        +item.value !== props.active &&
        item.class !== 'ellipsis' &&
        !item.disabled
      );
    };

    const getQuery = (item: PaginationElement) => ({
      ...route.query,
      page: +item.value < 2 ? undefined : item.value,
    });
    const getPath = () => route.path;

    return { paginationMask, onEvent, isLink, getQuery, getPath };
  },
});
</script>
<style scoped lang="scss" src="./PaginationScss.scss" />
